import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CATEGORIES, BATTERY_SUBTYPES, CATEGORY_LABEL, apiToUi } from "../lib/catalog";
import { toast } from "sonner";
import { CheckCircle2, Phone, MessageCircle } from "lucide-react";

const emptyForm = {
  full_name: "", phone: "", email: "", city_area: "",
  selected_brand: "", selected_model: "",
};

export default function LeadForm({ preselect }) {
  // Hydrate preselect from sessionStorage if the parent didn't pass one — this
  // covers the /find-battery/request navigation where the finder's React state
  // is lost across the route change.
  let effectivePreselect = preselect;
  if (!effectivePreselect?.product_category && typeof window !== "undefined") {
    try {
      const stored = window.sessionStorage.getItem("bbh_preselect");
      if (stored) effectivePreselect = JSON.parse(stored);
    } catch { /* storage unavailable */ }
  }
  // UI-level category + subtype (Batteries → automotive/two_wheeler/inverter)
  const initialUi = apiToUi(effectivePreselect?.product_category);
  const [category, setCategory] = useState(initialUi.category);
  const [subtype, setSubtype] = useState(initialUi.subtype);
  const [form, setForm] = useState({
    ...emptyForm,
    ...(effectivePreselect || {}),
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Keep in sync if preselect changes (e.g. after Recommend on the same page)
  useEffect(() => {
    if (!preselect) return;
    const ui = apiToUi(preselect.product_category);
    setCategory(ui.category);
    setSubtype(ui.subtype);
    setForm(f => ({ ...f, ...preselect }));
  }, [preselect]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: null })); };

  const apiCategory = category === "batteries" ? subtype : category;
  const preselectedCategory = effectivePreselect?.product_category;
  const preselected = Boolean(preselectedCategory);
  // When a customer arrives with a preselect (from the finder), always trust the
  // category that was captured — don't rederive it through apiToUi/dropdowns
  // which may be empty after a route change to /find-battery/request.
  const submitCategory = preselectedCategory || apiCategory;

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Name is required";
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.phone.trim())) e.phone = "Valid phone number required";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!preselected) {
      if (!category) e.product_category = "Please choose a category";
      else if (category === "batteries" && !subtype) e.product_category = "Please choose battery type";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, product_category: submitCategory });
      setDone(true);
      try { window.sessionStorage.removeItem("bbh_preselect"); } catch { /* ignore */ }
      toast.success("Request received. We will contact you shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Card className="bg-card border-black/10 p-8 md:p-10 rounded-2xl text-center" data-testid="lead-success">
        <CheckCircle2 className="mx-auto h-14 w-14 text-secondary mb-4" />
        <h3 className="font-display text-2xl md:text-3xl font-bold">Thank you. Your request has been received.</h3>
        <p className="text-muted-foreground mt-2">Our team will contact you shortly with the best product option.</p>
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a href="tel:+919549390000" data-testid="lead-success-call" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto rounded-full bg-primary text-primary-foreground hover:bg-secondary hover:text-white px-6 h-12">
              <Phone className="h-4 w-4 mr-1.5" /> Call Now
            </Button>
          </a>
          <a
            href="https://wa.me/919530498888"
            target="_blank"
            rel="noreferrer"
            data-testid="lead-success-whatsapp"
            className="w-full sm:w-auto"
          >
            <Button variant="outline" className="w-full sm:w-auto rounded-full border-black/15 hover:bg-black/5 px-6 h-12">
              <MessageCircle className="h-4 w-4 mr-1.5" /> Talk to Expert on WhatsApp
            </Button>
          </a>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">Prefer instant help? Reach us at the store from 10 am to 8 pm.</p>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-black/10 p-6 md:p-8 rounded-2xl" id="lead-form">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.24em] text-secondary font-bold">Request A Call Back</div>
        <h3 className="font-display text-2xl md:text-3xl font-bold mt-1">Talk to an Amaron expert</h3>
        <p className="text-sm text-muted-foreground mt-1">Share your details, we will call you back and help you pick the right Amaron product.</p>
      </div>

      <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" noValidate>
        <FormField label="Full Name*" error={errors.full_name}>
          <Input data-testid="lead-full-name" value={form.full_name} onChange={e => set("full_name", e.target.value)} placeholder="Your name" className="bg-muted/40 border-black/10 h-12" />
        </FormField>
        <FormField label="Contact Number*" error={errors.phone}>
          <Input data-testid="lead-phone" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="Enter contact number" className="bg-muted/40 border-black/10 h-12" />
        </FormField>
        <FormField label="Email (optional)" error={errors.email}>
          <Input data-testid="lead-email" type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@example.com" className="bg-muted/40 border-black/10 h-12" />
        </FormField>
        <FormField label="City / Area">
          <Input data-testid="lead-city" value={form.city_area} onChange={e => set("city_area", e.target.value)} placeholder="Please enter city / area name" className="bg-muted/40 border-black/10 h-12" />
        </FormField>
        {preselected ? (
          <div className="md:col-span-2" data-testid="lead-preselect-summary">
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Selected Product</label>
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-primary/10 border border-primary/30 px-4 py-3">
              <span className="text-sm font-semibold text-foreground">
                {CATEGORY_LABEL[apiCategory] || apiCategory}
              </span>
              {form.selected_brand && (
                <span className="text-sm text-muted-foreground">· {form.selected_brand}</span>
              )}
              {form.selected_model && (
                <span className="text-sm text-muted-foreground">· {form.selected_model}</span>
              )}
              <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-secondary">Auto-filled</span>
            </div>
          </div>
        ) : (
          <>
            <FormField label="Product Category*" error={errors.product_category}>
              <Select value={category} onValueChange={(v) => { setCategory(v); setSubtype(""); setErrors(e => ({ ...e, product_category: null })); }}>
                <SelectTrigger data-testid="lead-category" className="bg-muted/40 border-black/10 h-12">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-card border-black/10">
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value} data-testid={`lead-category-${c.value}`}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            {category === "batteries" ? (
              <FormField label="Battery Type*">
                <Select value={subtype} onValueChange={(v) => { setSubtype(v); setErrors(e => ({ ...e, product_category: null })); }}>
                  <SelectTrigger data-testid="lead-subtype" className="bg-muted/40 border-black/10 h-12">
                    <SelectValue placeholder="Select battery type" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-black/10">
                    {BATTERY_SUBTYPES.map(c => (
                      <SelectItem key={c.value} value={c.value} data-testid={`lead-subtype-${c.value}`}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            ) : (
              <FormField label="Selected Model">
                <Input data-testid="lead-model" value={form.selected_model} onChange={e => set("selected_model", e.target.value)} placeholder="e.g. Amaron FLO DIN50L" className="bg-muted/40 border-black/10 h-12" />
              </FormField>
            )}
          </>
        )}
        <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
          <Button type="submit" disabled={submitting} data-testid="lead-submit"
            className="rounded-full bg-primary text-primary-foreground hover:bg-secondary hover:text-white px-8 h-12 text-base font-semibold">
            {submitting ? "Sending…" : "Submit Request"}
          </Button>
          <p className="text-xs text-muted-foreground">By submitting, you agree to be contacted by our team.</p>
        </div>
      </form>
    </Card>
  );
}

function FormField({ label, error, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</label>
      <div className="mt-2">{children}</div>
      {error && <div className="text-xs text-destructive mt-1" data-testid="field-error">{error}</div>}
    </div>
  );
}
